import { Component, OnInit } from '@angular/core';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { take } from 'rxjs/operators';
import { Router } from '@angular/router';

import { AppIcons } from '../../../../core/icons/fa-icons';
import { Popup } from '../../../../core/shared/components/popup/popup';
import { DynamicFieldConfig } from '../../../../core/shared/dynamic-form/form-config.model';
import { PKL_GRP } from '../../../../core/shared/models/picklist.model';
import { PickListService } from '../../../../core/shared/services/picklist.service';
import { TableColumn, TableAction } from '../../../shared/models/table-models';
import { Setup } from '../../shared/service/setup';
import { Table } from '../../../shared/table/table';
import { BackButton } from '../../../shared/buttons/back-button';

@Component({
  selector: 'app-state',
  standalone: true,
  imports: [TranslatePipe, CommonModule, Table, Popup, BackButton],
  templateUrl: './state.html',
  styleUrl: './state.css',
})
export class State implements OnInit {

  icons = AppIcons;

  countrySeq!: number;

  list: any[] = [];
  columns: TableColumn[] = [];

  showPopup = false;
  isEditMode = false;
  isViewMode = false;
  formData: any = {};

  fields: DynamicFieldConfig[] = [];

  showDeletePopup = false;
  deleteMessage = '';
  selectedRow: any = null;

  constructor(
    private translate: TranslateService,
    private setup: Setup,
    private toastr: ToastrService,
    private picklist: PickListService,
    private route: ActivatedRoute,
     private router: Router  
  ) {}

  // ================= INIT =================
  ngOnInit(): void {
    this.route.paramMap.subscribe(params => {
      this.countrySeq = Number(params.get('id'));
      this.loadPicklists();
    });
  }
 actions: TableAction[] = [
    {
      icon: this.icons.edit,
      tooltip: 'COMMON.EDIT',
      class: 'action-edit',
      onClick: (row) => this.openPopup(row),
    },
    {
      icon: this.icons.view,
      tooltip: 'COMMON.VIEW',
      class: 'action-view',
      onClick: (row) => this.openPopup(row, true),
    },
    {
      icon: this.icons.delete,
      tooltip: 'COMMON.DELETE',
      class: 'action-delete',
      onClick: (row) => this.openDeletePopup(row),
    },
  ];

  // ================= DELETE CONFIRM =================
  openDeletePopup(row: any) {
    this.selectedRow = row;
    this.deleteMessage = this.translate.instant('SETUP.CONFIRM_DELETE_NAME', { name: row.name });
    this.showDeletePopup = true;
  }

  confirmDelete(): void {
    if (!this.selectedRow) return;
    this.delete(this.selectedRow);
    this.selectedRow = null;
  }

  // ================= PICKLIST =================
  loadPicklists() {
    this.picklist.loadCodes([PKL_GRP.COMMON_STATUS]);

    this.picklist.picklists$
      .pipe(take(1))
      .subscribe(() => {
        this.buildFields();
        this.buildColumns();
        this.loadData();
      });
  }

  // ================= FORM =================
  buildFields() {
    this.fields = [
      {
        key: 'stateSeq',
        label: 'SETUP.ID',
        type: 'text',
        hidden: true,
      },
      {
        key: 'name',
        label: 'COMMON.NAME',
        type: 'text',
        validations: { required: true },
      },
      {
        key: 'status',
        label: 'COMMON.STATUS',
        type: 'select',
        options$: this.picklist.getOptionsSync(PKL_GRP.COMMON_STATUS),
        validations: { required: true },
      },
      {
        key: 'description',
        label: 'COMMON.DESCRIPTION',
        type: 'textarea',
      },
    ];
  }

  // ================= TABLE =================
buildColumns() {
  this.columns = [
    { key: 'stateSeq', label: 'SETUP.ID' },

    {
      key: 'name',
      label: 'COMMON.NAME',
      type: 'link',
      onClick: (row: any) => this.goDistrict(row)
    },

    { key: 'description', label: 'COMMON.DESCRIPTION' },

    {
      key: 'status',
      label: 'COMMON.STATUS',
      type: 'badge',
      map: {
        706: 'COMMON.ACTIVE',
        707: 'COMMON.INACTIVE'
      },
      badgeMap: {
        706: 'status-active',
        707: 'status-inactive'
      }
    }
  ];
}

  // ================= LOAD DATA =================
loadData() {
  this.setup.getStates(this.countrySeq).subscribe({
    next: (res: any[]) => {
      this.list = (res || []).map(s => ({
        stateSeq: s.stSeq,
        name: s.stNm,
        description: s.stDscr,
        status: s.stStsKey   // keep raw 706 / 707
      }));
    }
  });
}
getStatusLabel(key: number): string {
  switch (key) {
    case 706:
      return 'Active';
    case 707:
      return 'Inactive';
    default:
      return 'Unknown';
  }
}
  // ================= POPUP =================
  openPopup(data?: any, viewMode: boolean = false) {
    this.showPopup = true;
    this.isViewMode = viewMode;

    if (data) {
      this.isEditMode = true;

      this.formData = {
        stateSeq: data.stateSeq,
        name: data.name,
        description: data.description,
        status: data.status
      };
    } else {
      this.isEditMode = false;

      this.formData = {
        stateSeq: null,
        status: this.getDefaultStatus()
      };
    }
  }

  getDefaultStatus(): number {
    const options = this.picklist.getOptionsSync(PKL_GRP.COMMON_STATUS);
    return options?.[0]?.value || null;
  }

  // ================= SAVE =================
  handleSave = (form: any) => {

    const payload = {
      stSeq: this.isEditMode ? form.stateSeq : null,
      cntrySeq: this.countrySeq,
      stCd: '',
      stDscr: form.description,
      stNm: form.name,
      stStsKey: form.status,   // 🔥 direct dropdown value
      delFlg: 0,
      tntKey: 0
    };

    return this.setup.saveState(payload).subscribe({
      next: () => {
        this.toastr.success(this.translate.instant(this.isEditMode ? 'SETUP.GEOGRAPHY.STATE_UPDATED' : 'SETUP.GEOGRAPHY.STATE_CREATED'));
        this.showPopup = false;
        this.loadData();
      },
      error: () => this.toastr.error(this.translate.instant('SETUP.SAVE_FAILED'))
    });
  };

  // ================= DELETE =================
delete(row: any) {

  const payload = {
    stSeq: row.stateSeq,
    cntrySeq: this.countrySeq,   // ✅ REQUIRED
    delFlg: 1
  };

  this.setup.saveState(payload).subscribe({
    next: () => {
      this.toastr.success(this.translate.instant('SETUP.DELETED_SUCCESSFULLY'));
      this.loadData();
    },
    error: () => this.toastr.error(this.translate.instant('SETUP.DELETE_FAILED')),
  });
}
goDistrict(row: any) {
  this.router.navigate(['/district', row.stateSeq]);
}
}